/**
 * *Add kits…*'s contents and drops tables, headless (V1 completion R4): the shapes the request body holds them in,
 * what the dry run hints beside them, and the edits the web shell's widget makes. Orders decides every rule (the
 * folding, the checks, the addresses); this only reads the hints and keeps the body consistent while a person edits
 * it, which is the one rule worth restating: a drop names a content by its position, so taking a content out moves
 * the ones after it.
 */
import type { FieldHint } from "./object-ui/contract";

/** One thing one kit holds, as the request body carries it (`KitContentRequest`). */
export interface KitContentBody {
  line_ref?: string | null;
  stock_item_ref?: string | null;
  version_label?: string | null;
  per_kit?: number | null;
}

/** How one drop's kit differs for one content (`DropVariationRequest`). */
export interface DropVariationBody {
  content: number;
  version_label?: string | null;
  per_kit?: number | null;
}

/** One drop, as the request body carries it (`KitDropRequest`). */
export interface KitDropBody {
  address_ref?: string | null;
  name?: string | null;
  address?: string | null;
  town?: string | null;
  postcode?: string | null;
  country_code?: string | null;
  kits?: number | null;
  variations?: DropVariationBody[];
}

/** What a drop's address resolved to: one of the customer's, one to create, or nothing yet. */
export type DropPlaceState = "matched" | "to_create" | "unresolved";

/** The dry run's hint for one content row: its name, where it comes from, and the versions a drop may name. */
export interface ContentHint {
  label: string;
  from_label: string | null;
  /** A line with several versions that each drop names its own of; null when every kit takes the same. */
  versions: string[] | null;
}

/** The contents table's hints, one per content row sent. */
export function contentHints(
  hints: Record<string, FieldHint>,
): readonly ContentHint[] {
  const context = hints.contents?.widget_context as
    { rows?: ContentHint[] } | null | undefined;
  return context?.rows ?? [];
}

/** Where each drop's address resolved to, in the order the drops were sent. */
export function dropPlaces(
  hints: Record<string, FieldHint>,
): readonly DropPlaceState[] {
  const context = hints.drops?.widget_context as
    { places?: DropPlaceState[] } | null | undefined;
  return context?.places ?? [];
}

/** What the drops say of the order they were copied from ("copied from ORD-K58816 (September): 3 removed, 1 added"). */
export function dropsCopiedFrom(
  hints: Record<string, FieldHint>,
): string | null {
  const context = hints.drops?.widget_context as
    { copied_from?: string | null } | null | undefined;
  return context?.copied_from ?? null;
}

/** How a content row reads when the dry run has not hinted it yet (a row just added). */
export function contentFallbackLabel(content: KitContentBody): string {
  return content.line_ref ?? content.stock_item_ref ?? "";
}

/** The version a drop names for one content, or null when it names none. */
export function dropVersion(
  drop: KitDropBody,
  contentIndex: number,
): string | null {
  return (
    drop.variations?.find((v) => v.content === contentIndex)?.version_label ??
    null
  );
}

/** The drop naming ``versionLabel`` for one content (null or empty: naming none). */
export function withDropVersion(
  drop: KitDropBody,
  contentIndex: number,
  versionLabel: string | null,
): KitDropBody {
  const others = (drop.variations ?? []).filter(
    (v) => v.content !== contentIndex,
  );
  const label = versionLabel?.trim() || null;
  const own = drop.variations?.find((v) => v.content === contentIndex);
  const kept =
    label === null && own?.per_kit == null
      ? others
      : [...others, { ...own, content: contentIndex, version_label: label }];
  return {
    ...drop,
    variations: kept.sort((a, b) => a.content - b.content),
  };
}

/**
 * The contents and drops with content ``index`` taken out: the drops' variations that named it go, and those naming a
 * later content follow it up by one (a drop names a content by its position).
 */
export function withoutContent(
  contents: readonly KitContentBody[],
  drops: readonly KitDropBody[],
  index: number,
): { contents: KitContentBody[]; drops: KitDropBody[] } {
  return {
    contents: contents.filter((_, i) => i !== index),
    drops: drops.map((drop) =>
      drop.variations && drop.variations.length > 0
        ? {
            ...drop,
            variations: drop.variations
              .filter((v) => v.content !== index)
              .map((v) =>
                v.content > index ? { ...v, content: v.content - 1 } : v,
              ),
          }
        : drop,
    ),
  };
}

/** How many drops and how many kits the list holds in all. */
export function dropTotals(drops: readonly KitDropBody[]): {
  drops: number;
  kits: number;
} {
  return {
    drops: drops.length,
    kits: drops.reduce((sum, d) => sum + (d.kits ?? 0), 0),
  };
}

/** A number typed into a count box: a whole number of at least 0, else null (left empty). */
export function wholeNumberOrNull(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

/** The drop's place as the table names it: its name, else its address reference, else the row's number. */
export function dropLabel(drop: KitDropBody, index: number): string {
  return drop.name?.trim() || drop.address_ref?.trim() || `Row ${index + 1}`;
}
